/**
 * Extension Bridge Service
 * 
 * Handles communication between MobyNet and the browser extension worker pool.
 * Provides a clean API for invoking extension operations and managing callbacks.
 */

// NetworkMetrics interface for extension operations
export interface NetworkMetrics {
  uploadSpeed: number;
  downloadSpeed: number;
  latency: number;
  packetLoss: number;
}

export interface ExtensionOperation {
  type:
    | 'PROCESS_FILE'
    | 'INITIALIZE_TRANSFER_METADATA'
    | 'INITIALIZE_RECEPTION_METADATA'
    | 'ROUTE_CHUNK'
    | 'ROUTE_CHUNK_SEGMENT'
    | 'ASSEMBLE_CHUNK_SEGMENTS'
    | 'RESUME_TRANSFER'
    | 'REASSEMBLE_FILE'
    | 'GET_PROGRESS'
    | 'CLEANUP'
    | 'SAVE_TO_NATIVE'
    | 'CREATE_CHECKPOINT'
    | 'RESTORE_CHECKPOINT'
    // Gap operations actually used in this file
    | 'INITIALIZE_PROGRESS_TRACKING'
    | 'INITIALIZE_ASSEMBLY'
    | 'TRANSFER_CHUNK'
    | 'GET_ASSEMBLY_PROGRESS'
    | 'FINALIZE_ASSEMBLY'
    | 'CLEANUP_ASSEMBLY'
    | 'GET_MASSIVE_FILE_PROGRESS'
    | 'CLEANUP_PROGRESS_TRACKING'
    | 'CLEAR_VALIDATION_CACHE';
  data: any;
  transferId: string;
}

export interface ExtensionResponse {
  success: boolean;
  data?: any;
  error?: string;
  transferId: string;
}

export interface ProgressUpdate {
  transferId: string;
  completedChunks: number;
  totalChunks: number;
  bytesTransferred: number;
  totalBytes: number;
  currentSpeed: number; // bytes/sec
  estimatedTimeRemaining: number; // seconds
  checkpointsCreated: number;
}

export interface CheckpointUpdate {
  transferId: string;
  checkpointPath: string;
  completedChunks: number[];
  timestamp: number;
  resumable: boolean;
}

export interface AssemblyProgress {
  transferId: string;
  fileHash: string;
  phase: 'assembling' | 'verifying' | 'complete' | 'error';
  progress: number; // 0-100
  outputPath?: string;
  error?: string;
}

export type ProgressCallback = (update: ProgressUpdate) => void;
export type CheckpointCallback = (update: CheckpointUpdate) => void;
export type AssemblyCallback = (progress: AssemblyProgress) => void;

/**
 * Service for communicating with browser extension worker pool
 */
export class ExtensionBridge {
  private progressCallbacks = new Set<ProgressCallback>();
  private checkpointCallbacks = new Set<CheckpointCallback>();
  private assemblyCallbacks = new Set<AssemblyCallback>();
  private activeOperations = new Map<string, { resolve: Function; reject: Function; timeout: NodeJS.Timeout }>();
  private _isExtensionAvailable: boolean = false;
  private keepAliveActive: boolean = false;
  private keepAliveIntervalMs: number = 20000;
  private lastActivationCheck: number = 0;
  private activationCheckIntervalMs: number = 15000;
  private readonly EXTENSION_SIZE_THRESHOLD: number = 100 * 1024 * 1024; // 100MB
  private disposed: boolean = false;
  private boundHandleExtensionMessage?: (event: MessageEvent) => void;
  private boundHandleChromeExtensionMessage?: (message: any, sender: any, sendResponse: Function) => void;
  private extensionReadyListener?: () => void;
  private senderFileReferences = new Map<string, File>(); // Store File references for on-demand chunk reading
  
  constructor() {
    // Prepare bound listeners so we can remove them later in dispose()
    this.boundHandleExtensionMessage = this.handleExtensionMessage.bind(this);
    this.boundHandleChromeExtensionMessage = this.handleChromeExtensionMessage.bind(this);
    this.extensionReadyListener = () => {
      this._isExtensionAvailable = true;
      // Re-verify via ping to confirm background connectivity
      this.detectExtensionCapability();
    };

    this.setupEventListeners();
    this.detectExtensionCapability();
  }

  /**
   * Start/stop keepalive via page bridge to hold MV3 service worker awake
   */
  public async startKeepAlive(intervalMs?: number): Promise<boolean> {
    try {
      const ext = (window as any).mobyNetExtension;
      if (!ext?.keepAliveStart) return false;
      const ms = intervalMs ?? this.keepAliveIntervalMs;
      await ext.keepAliveStart(ms);
      this.keepAliveActive = true;
      this.keepAliveIntervalMs = ms;
      console.log('[ExtensionBridge] Keepalive started', { intervalMs: ms });
      return true;
    } catch (e) {
      console.warn('[ExtensionBridge] Failed to start keepalive:', e);
      return false;
    }
  }

  public async stopKeepAlive(): Promise<void> {
    try {
      const ext = (window as any).mobyNetExtension;
      if (ext?.keepAliveStop && this.keepAliveActive) {
        await ext.keepAliveStop();
        console.log('[ExtensionBridge] Keepalive stopped');
      }
    } catch (e) {
      // non-critical
    } finally {
      this.keepAliveActive = false;
    }
  }
  
  /**
   * Check if extension is available for processing
   */
  get isExtensionAvailable(): boolean {
    return this._isExtensionAvailable;
  }

  /**
   * Local capability snapshot for negotiation with peers
   */
  public getLocalCapabilities(): { extensionAvailable: boolean; sizeThresholdBytes: number } {
    return {
      extensionAvailable: this._isExtensionAvailable,
      sizeThresholdBytes: this.EXTENSION_SIZE_THRESHOLD,
    };
  }
  
  /**
   * Detect extension capability and availability
   */
  private async detectExtensionCapability(): Promise<void> {
    try {
      // Prefer the injected page-bridge API exposed on window
      const extension = (window as any).mobyNetExtension;
      if (extension?.isAvailable && typeof extension.ping === 'function') {
        const response = await this.pingExtension();
        this._isExtensionAvailable = response.success;
        console.log('[ExtensionBridge] Extension availability:', this._isExtensionAvailable);
      } else {
        console.log('[ExtensionBridge] Extension API not available on window');
        this._isExtensionAvailable = false;
      }
    } catch (error) {
      console.warn('[ExtensionBridge] Extension detection failed:', error);
      this._isExtensionAvailable = false;
    }
  }

  /**
   * Ensure the extension service worker is awake and reachable.
   * Uses ping with a cooldown to avoid excessive traffic.
   */
  public async ensureActivated(options?: { force?: boolean }): Promise<boolean> {
    try {
      const extension = (window as any).mobyNetExtension;
      if (!extension?.isAvailable || typeof extension.ping !== 'function') {
        this._isExtensionAvailable = false;
        return false;
      }

      const now = Date.now();
      if (!options?.force && now - this.lastActivationCheck < this.activationCheckIntervalMs) {
        return this._isExtensionAvailable;
      }

      const response = await this.pingExtension();
      this.lastActivationCheck = now;
      this._isExtensionAvailable = response.success === true;
      if (!this._isExtensionAvailable) {
        console.warn('[ExtensionBridge] ensureActivated: ping failed');
      }
      return this._isExtensionAvailable;
    } catch (e) {
      this._isExtensionAvailable = false;
      return false;
    }
  }
  
  /**
   * Ping extension to verify it's available
   */
  private async pingExtension(): Promise<{ success: boolean; version?: string; status?: string; timestamp?: number }> {
    const extension = (window as any).mobyNetExtension;
    if (!extension?.ping) return { success: false };

    // Inline helpers to avoid separate class changes
    const classify = (err: any): { transient: boolean; code: string; message: string } => {
      const raw = err?.message || String(err || '');
      const msg = raw.toLowerCase();
      if (msg.includes('extension context invalidated')) return { transient: true, code: 'context_invalidated', message: raw };
      if (msg.includes('message port closed') || msg.includes('before a response was received')) return { transient: true, code: 'message_port_closed', message: raw };
      if (msg.includes('receiving end does not exist') || msg.includes('could not establish connection')) return { transient: true, code: 'receiving_end_missing', message: raw };
      if (msg.includes('timeout')) return { transient: true, code: 'timeout', message: raw };
      return { transient: false, code: 'permanent', message: raw };
    };
    const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

    const attempts = 3;
    let lastError: any = null;
    for (let i = 0; i < attempts; i++) {
      if (i > 0) {
        // Backoff: 150ms, 400ms
        await sleep(i === 1 ? 150 : 400);
      }
      try {
        const res = await extension.ping();
        if (res && res.version) {
          return { success: true, version: res.version, status: res.status, timestamp: res.timestamp };
        }
        // Invalid shape - treat as transient once
        lastError = new Error('Invalid ping response');
        const kind = classify(lastError);
        if (!kind.transient) break;

        if (!this.keepAliveActive && typeof extension.keepAliveStart === 'function') {
          try {
            await extension.keepAliveStart(this.keepAliveIntervalMs);
            this.keepAliveActive = true;
          } catch (_) { /* non-fatal */ }
        }
      } catch (e) {
        lastError = e;
        const kind = classify(e);
        if (kind.transient) {
          if (!this.keepAliveActive && typeof extension.keepAliveStart === 'function') {
            try {
              await extension.keepAliveStart(this.keepAliveIntervalMs);
              this.keepAliveActive = true;
            } catch (_) { /* non-fatal */ }
          }
          continue;
        } else {
          break;
        }
      }
    }
    console.warn('[ExtensionBridge] Ping failed', lastError ? { error: (lastError.message || String(lastError)) } : undefined);
    return { success: false };
  }

  /**
   * Setup event listeners for extension responses
   */
  private setupEventListeners(): void {
    // Listen for extension messages
    if (typeof window !== 'undefined') {
      if (this.boundHandleExtensionMessage) {
        window.addEventListener('message', this.boundHandleExtensionMessage);
      }
      // Listen for extension ready signal from page bridge
      if (this.extensionReadyListener) {
        window.addEventListener('mobynet-extension-ready', this.extensionReadyListener);
      }
      
      // Listen for chrome extension messages if available
      if ('chrome' in window && (window as any).chrome.runtime) {
        try {
          if (this.boundHandleChromeExtensionMessage) {
            (window as any).chrome.runtime.onMessage.addListener(
              this.boundHandleChromeExtensionMessage
            );
          }
        } catch (error) {
          console.warn('[ExtensionBridge] Chrome runtime listener setup failed:', error);
        }
      }
    }
  }

  /**
   * Handle messages from extension
   */
  private handleExtensionMessage(event: MessageEvent): void {
    if (this.disposed) return;
    if (event.source !== window) {
      return;
    }

    const { type, data, payload, origin } = (event as any).data || {};
    if (!type) return;

    // Ignore generic response envelopes coming from the content script bridge
    // These are page-level responses (MOBY_RESPONSE) not extension worker signals
    if (type === 'MOBY_RESPONSE' && origin === 'moby-content-script') {
      // Silently ignore to avoid noisy warnings
      return;
    }

    // Ignore our own page-bridge request messages.
    // page-bridge.js posts messages like MOBY_PING, MOBY_GET_CAPABILITIES, MOBY_KEEPALIVE_START
    // to the window for the content script to pick up. This listener sees those too,
    // but they are not extension worker push events, so ignore them to avoid warnings.
    if (typeof type === 'string' && type.startsWith('MOBY_') && type !== 'MOBY_RESPONSE' && !type.startsWith('MOBY_EXT_')) {
      return;
    }

    switch (type) {
      case 'MOBY_EXT_OPERATION_RESPONSE':
        this.handleOperationResponse(data);
        break;
      case 'MOBY_EXT_PROGRESS_UPDATE':
        this.handleProgressUpdate(data);
        break;
      case 'MOBY_EXT_CHECKPOINT_UPDATE':
        this.handleCheckpointUpdate(data);
        break;
      case 'MOBY_EXT_ASSEMBLY_PROGRESS':
        this.handleAssemblyProgress(data);
        break;
      case 'MASSIVE_FILE_PROGRESS_UPDATE': {
        const update = this.mapMassiveProgressToUpdate(payload?.progress || data?.progress || data);
        this.handleProgressUpdate(update);
        break;
      }
      case 'MASSIVE_FILE_TRANSFER_COMPLETE':
      case 'MASSIVE_FILE_CLEANUP_COMPLETE':
        this.handleCompletionSignal((event as any).data);
        break;
      default:
        console.warn('[ExtensionBridge] Unknown extension message type:', type);
    }
  }

  /**
   * Handle Chrome extension messages
   */
  private handleChromeExtensionMessage(message: any, sender: any, sendResponse: Function): void {
    if (this.disposed) return;
    if (message.source === 'moby-extension') {
      // Create a proper MessageEvent-like object and convert via unknown
      const messageEvent = {
        data: message,
        source: window,
        origin: window.location.origin,
        lastEventId: '',
        ports: [],
        type: 'message'
      } as unknown as MessageEvent;
      this.handleExtensionMessage(messageEvent);
    }
    
    // GAP #3: Handle completion signaling from ExtensionCompletionSignaler
    if (message.source === 'extension-completion-signaler') {
      this.handleCompletionSignal(message);
    }
  }

  /**
   * Handle operation responses from extension
   */
  private handleOperationResponse(response: ExtensionResponse): void {
    if (this.disposed) return;
    const operation = this.activeOperations.get(response.transferId);
    if (!operation) {
      console.warn('[ExtensionBridge] Received response for unknown operation:', response.transferId);
      return;
    }

    clearTimeout(operation.timeout);
    this.activeOperations.delete(response.transferId);

    if (response.success) {
      // FIX: Return the full response if no data field exists (for simple success responses)
      // This fixes receiver streaming initialization where extension returns {success: true}
      operation.resolve(response.data !== undefined ? response.data : response);
    } else {
      operation.reject(new Error(response.error || 'Extension operation failed'));
    }
  }

  /**
   * Handle progress updates from extension
   */
  private handleProgressUpdate(update: ProgressUpdate): void {
    if (this.disposed) return;
    console.log('[ExtensionBridge] Progress update:', {
      transferId: update.transferId.slice(0, 8) + '...',
      progress: `${update.completedChunks}/${update.totalChunks}`,
      speed: this.formatSpeed(update.currentSpeed),
      eta: `${Math.round(update.estimatedTimeRemaining)}s`
    });

    this.progressCallbacks.forEach(callback => {
      try {
        callback(update);
      } catch (error) {
        console.error('[ExtensionBridge] Error in progress callback:', error);
      }
    });
  }

  /**
   * Handle checkpoint updates from extension
   */
  private handleCheckpointUpdate(update: CheckpointUpdate): void {
    if (this.disposed) return;
    console.log('[ExtensionBridge] Checkpoint created:', {
      transferId: update.transferId.slice(0, 8) + '...',
      checkpointPath: update.checkpointPath,
      completedChunks: update.completedChunks.length,
      resumable: update.resumable
    });

    this.checkpointCallbacks.forEach(callback => {
      try {
        callback(update);
      } catch (error) {
        console.error('[ExtensionBridge] Error in checkpoint callback:', error);
      }
    });
  }

  /**
   * Handle assembly progress from extension
   */
  private handleAssemblyProgress(progress: AssemblyProgress): void {
    if (this.disposed) return;
    console.log('[ExtensionBridge] Assembly progress:', {
      transferId: progress.transferId.slice(0, 8) + '...',
      phase: progress.phase,
      progress: `${progress.progress}%`,
      outputPath: progress.outputPath
    });

    this.assemblyCallbacks.forEach(callback => {
      try {
        callback(progress);
      } catch (error) {
        console.error('[ExtensionBridge] Error in assembly callback:', error);
      }
    });
  }

  /**
   * Map extension massive-file progress to internal ProgressUpdate shape
   */
  private mapMassiveProgressToUpdate(progress: any): ProgressUpdate {
    const totalChunks = progress?.totalChunks ?? 0;
    const chunksReceived = progress?.chunksReceived ?? 0;
    const totalBytes = progress?.totalSize ?? 0;
    const bytesTransferred = totalChunks > 0 ? Math.round((chunksReceived / totalChunks) * totalBytes) : 0;
    const eta = progress?.estimatedCompletion ? Math.max(0, Math.round((progress.estimatedCompletion - Date.now()) / 1000)) : 0;

    return {
      transferId: progress?.transferId || '',
      completedChunks: chunksReceived,
      totalChunks,
      bytesTransferred,
      totalBytes,
      currentSpeed: 0,
      estimatedTimeRemaining: eta,
      checkpointsCreated: 0
    };
  }

  /**
   * GAP #3: Handle completion signals from ExtensionCompletionSignaler
   */
  private handleCompletionSignal(message: any): void {
    if (this.disposed) return;
    if (message.type === 'MASSIVE_FILE_TRANSFER_COMPLETE') {
      const completionData = message.payload;
      
      console.log('[ExtensionBridge] Massive file transfer complete:', {
        transferId: completionData.transferId.slice(0, 8) + '...',
        fileName: completionData.fileMetadata.fileName,
        fileSize: this.formatSize(completionData.fileMetadata.totalSize),
        downloadReady: completionData.nativeLocation.downloadReady
      });

      const finalizeProgressNotify = () => {
        this.progressCallbacks.forEach(callback => {
          try {
            callback({
              transferId: completionData.transferId,
              completedChunks: completionData.fileMetadata.totalChunks || 0,
              totalChunks: completionData.fileMetadata.totalChunks || 0,
              bytesTransferred: completionData.fileMetadata.totalSize,
              totalBytes: completionData.fileMetadata.totalSize,
              currentSpeed: 0,
              estimatedTimeRemaining: 0,
              checkpointsCreated: 0
            });
          } catch (error) {
            console.error('[ExtensionBridge] Error in completion progress callback:', error);
          }
        });
      };

      const doCleanup = async () => {
        try {
          await this.cleanup(completionData.transferId);
        } catch (e) {
          console.warn('[ExtensionBridge] Cleanup after completion warning:', e);
        }
      };

      // Trigger download via chrome.runtime.sendMessage to extension if requested
      if (completionData.downloadAction.triggerDownload) {
        this.triggerExtensionDownload(completionData.transferId)
          .then(() => {
            console.log('[ExtensionBridge] Download trigger sent successfully');
          })
          .catch(error => {
            console.error('[ExtensionBridge] Download trigger failed:', error);
          })
          .finally(() => {
            finalizeProgressNotify();
            // Ensure cleanup and keepalive stop even if download trigger failed
            void doCleanup();
          });
      } else {
        // No download requested; still notify and cleanup
        finalizeProgressNotify();
        void doCleanup();
      }
    } else if (message.type === 'MASSIVE_FILE_CLEANUP_COMPLETE') {
      const cleanupData = message.payload;
      console.log('[ExtensionBridge] Transfer cleanup complete:', {
        transferId: cleanupData.transferId.slice(0, 8) + '...'
      });
    }
  }

  /**
   * GAP #3: Trigger download via extension
   */
  private async triggerExtensionDownload(transferId: string): Promise<void> {
    if ('chrome' in window && (window as any).chrome?.runtime) {
      try {
        await (window as any).chrome.runtime.sendMessage({
          type: 'REQUEST_NATIVE_FILE_DOWNLOAD',
          transferId,
          source: 'moby-browser'
        });
      } catch (error) {
        console.error('[ExtensionBridge] Failed to trigger download:', error);
        throw error;
      }
    } else {
      console.warn('[ExtensionBridge] Chrome runtime not available for download trigger');
    }
  }

  /**
   * Invoke an extension worker operation
   */
  async invokeExtensionWorker(
    operation: string,
    data: any,
    timeout: number = 30000
  ): Promise<any> {
    
    const transferId = `bridge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return new Promise((resolve, reject) => {
      // Setup timeout
      const timeoutHandle = setTimeout(() => {
        this.activeOperations.delete(transferId);
        reject(new Error(`Extension operation timeout: ${operation}`));
      }, timeout);

      // Store operation for response handling
      this.activeOperations.set(transferId, { resolve, reject, timeout: timeoutHandle });

      // Send message to extension
      const message: ExtensionOperation = {
        type: operation as any,
        data,
        transferId
      };

      this.sendToExtension(message);
    });
  }

  /**
   * Initialize metadata-only transfer setup for extension coordination
   * Extension prepares for native file handling, browser handles chunk routing
   */
  async initializeTransferMetadata(
    transferId: string,
    metadata: {
      fileName: string;
      fileSize: number;
      totalChunks: number;
      rootHash: string;
      direction: 'sender';
      peerAddress: string;
      checkpointInterval?: number;
      priorityLevel?: 'high' | 'normal' | 'low';
    }
  ): Promise<{
    success: boolean;
    transferId: string;
    nativeFilePath?: string;
    checkpointPath?: string;
  }> {
    
    console.log('[ExtensionBridge] Initializing transfer metadata sync', {
      transferId: transferId.slice(0, 8) + '...',
      fileName: metadata.fileName,
      fileSize: metadata.fileSize,
      direction: metadata.direction
    });

    // Pre-transfer capability negotiation
    const EXTENSION_SIZE_THRESHOLD = this.EXTENSION_SIZE_THRESHOLD;
    // Force activation check to avoid stale availability and throw to trigger provider fallback
    await this.ensureActivated({ force: true });
    if (!this._isExtensionAvailable) {
      console.log('[ExtensionBridge] Sender-side init failed: extension unavailable');
      throw new Error('Extension not available for sender initialization');
    }
    if (metadata.fileSize < EXTENSION_SIZE_THRESHOLD) {
      console.log('[ExtensionBridge] Sender-side init rejected: file below extension threshold');
      throw new Error('File below extension threshold for extension path');
    }

    // Keep SW awake during transfer coordination
    if (!this.keepAliveActive) {
      await this.startKeepAlive();
    }

    // Initialize progress tracking on extension for visibility (Gap #4)
    await this.invokeExtensionWorker('INITIALIZE_PROGRESS_TRACKING', {
      transferId,
      metadata: {
        fileName: metadata.fileName,
        totalSize: metadata.fileSize,
        totalChunks: metadata.totalChunks
      }
    });

    // CRITICAL FIX: Sender MUST create offscreen document for native file reading
    // Initialize sender-side streaming with offscreen document creation
    console.log('[ExtensionBridge] Creating offscreen document for sender-side NFS streaming');
    const result = await this.invokeExtensionWorker('INITIALIZE_ASSEMBLY', {
      transferId,
      metadata: {
        fileName: metadata.fileName,
        totalSize: metadata.fileSize,
        totalChunks: metadata.totalChunks,
        rootHash: metadata.rootHash,
        direction: metadata.direction,
        peerAddress: metadata.peerAddress,
        checkpointInterval: metadata.checkpointInterval || 10
      }
    });

    console.log('[ExtensionBridge] Sender offscreen document created', result);
    return result;
  }

  /**
   * Initialize metadata-only reception setup for extension coordination
   * Extension prepares native file assembly structure, browser handles chunk routing
   */
  /**
   * Initialize progressive assembly for massive file reception
   * Sets up native file assembly structure in extension
   */
  async initializeReceptionMetadata(
    transferId: string,
    metadata: {
      fileName: string;
      fileSize: number;
      totalChunks: number;
      rootHash: string;
      direction: 'receiver';
      senderAddress: string;
      checkpointInterval?: number;
      priorityLevel?: 'high' | 'normal' | 'low';
    }
  ): Promise<{
    success: boolean;
    transferId: string;
    nativeFilePath?: string;
    checkpointPath?: string;
    merkleTreePath?: string;
  }> {
    
    console.log('[ExtensionBridge] Initializing progressive assembly for massive file', {
      transferId: transferId.slice(0, 8) + '...',
      fileName: metadata.fileName,
      fileSize: metadata.fileSize,
      totalChunks: metadata.totalChunks,
      direction: metadata.direction
    });

    // Pre-transfer capability negotiation
    const EXTENSION_SIZE_THRESHOLD = this.EXTENSION_SIZE_THRESHOLD;
    
    if (metadata.fileSize < EXTENSION_SIZE_THRESHOLD) {
      console.log('[ExtensionBridge] File below extension threshold for extension reception');
      throw new Error('File below extension threshold for receiver initialization');
    }
    
    // Ensure extension is active and available
    await this.ensureActivated({ force: true });
    if (!this._isExtensionAvailable) {
      console.log('[ExtensionBridge] Extension not available for receiver initialization');
      throw new Error('Extension not available for receiver initialization');
    }
    
    console.log('[ExtensionBridge] Initializing progressive assembly in extension');
    
    // Hold SW awake during transfer
    if (!this.keepAliveActive) {
      await this.startKeepAlive();
    }
    
    // Initialize massive file progress tracking (Gap #4)
    await this.invokeExtensionWorker('INITIALIZE_PROGRESS_TRACKING', {
      transferId,
      metadata: {
        fileName: metadata.fileName,
        totalSize: metadata.fileSize,
        totalChunks: metadata.totalChunks
      }
    });

    // Use the new progressive assembly initialization
    return await this.invokeExtensionWorker('INITIALIZE_ASSEMBLY', {
      transferId,
      metadata: {
        fileName: metadata.fileName,
        totalSize: metadata.fileSize,
        totalChunks: metadata.totalChunks,
        rootHash: metadata.rootHash,
        direction: metadata.direction,
        senderAddress: metadata.senderAddress,
        checkpointInterval: metadata.checkpointInterval || 10
      }
    });
  }

  /**
   * Route chunk data to extension for progressive assembly
   * Uses new progressive assembly system with automatic checkpointing
   * ENFORCES: Handshake completion and valid chunk indices (0-based)
   */
  async routeChunkToExtension(
    transferId: string,
    chunkIndex: number,
    chunkData: ArrayBuffer,
    metadata: {
      isLastChunk: boolean;
      totalChunks: number;
      chunkHash?: string;
      merkleProof?: any;
    }
  ): Promise<{
    success: boolean;
    stored: boolean;
    needsCheckpoint?: boolean;
    progress?: number;
  }> {
    
    // HANDSHAKE ENFORCEMENT: Validate chunk index is 0-based and within bounds
    if (chunkIndex < 0 || chunkIndex >= metadata.totalChunks) {
      throw new Error(`Invalid chunk index ${chunkIndex} for transfer ${transferId} (total: ${metadata.totalChunks})`);
    }
    
    console.log('[ExtensionBridge] Routing chunk to extension', {
      transferId: transferId.slice(0, 8) + '...',
      chunkIndex,
      chunkSize: chunkData.byteLength,
      isLastChunk: metadata.isLastChunk,
      validIndex: chunkIndex >= 0 && chunkIndex < metadata.totalChunks
    });

    // Check extension availability (lazy re-activation if stale)
    await this.ensureActivated();
    if (!this.isExtensionAvailable) {
      throw new Error('Extension not available for chunk routing');
    }

    // Use new progressive assembly system - automatically handles chunking and checkpointing
    console.log('[ExtensionBridge] Processing chunk via progressive assembly', {
      chunkSize: chunkData.byteLength,
      progress: `${chunkIndex + 1}/${metadata.totalChunks}`
    });

    // Convert ArrayBuffer to Uint8Array for extension processing
    const chunkUint8Array = new Uint8Array(chunkData);
    
    // Route to progressive assembly system
    const result = await this.invokeExtensionWorker('TRANSFER_CHUNK', {
      transferId,
      chunkIndex,
      chunkData: chunkUint8Array,
      chunkMetadata: {
        isLastChunk: metadata.isLastChunk,
        totalChunks: metadata.totalChunks,
        chunkHash: metadata.chunkHash,
        merkleProof: metadata.merkleProof,
        size: chunkData.byteLength
      }
    });

    // Transform response to match expected interface
    // handleOperationResponse resolves with response.data, so "result" is usually the data payload
    // Support both shapes just in case: {success, data:{...}} or direct data object
    const dataPayload: any = (result && typeof result === 'object' && 'data' in result && (result as any).data)
      ? (result as any).data
      : result;
    const needsFlush: boolean = !!(dataPayload?.needsFlush);
    const chunksReceived: number = typeof dataPayload?.chunksReceived === 'number' ? dataPayload.chunksReceived : (metadata.isLastChunk ? metadata.totalChunks : (chunkIndex + 1));
    const progressPct = metadata.totalChunks > 0 ? (chunksReceived / metadata.totalChunks) * 100 : 0;
    return {
      success: true,
      stored: true,
      needsCheckpoint: needsFlush,
      progress: progressPct
    };
  }

  /**
   * Legacy processFile method - DEPRECATED for massive files
   * Use initializeTransfer/initializeReception + routeChunkToExtension instead
   * Kept for compatibility with smaller files that can be processed in single operations
   */
  async processFile(
    file: File,
    options: {
      peerId: string;
      enableDeduplication?: boolean;
      checkpointInterval?: number;
      maxMemoryUsage?: number;
      priorityLevel?: 'high' | 'normal' | 'low';
    }
  ): Promise<{
    hash: string;
    size: number;
    chunkInfo: any;
    checkpointPath?: string;
  }> {
    
    console.log('[ExtensionBridge] DEPRECATED: processFile() called - use chunked routing for massive files', {
      fileName: file.name,
      fileSize: file.size,
      peerId: options.peerId.slice(0, 8) + '...',
    });

    // Pre-transfer capability negotiation
    const EXTENSION_SIZE_THRESHOLD = this.EXTENSION_SIZE_THRESHOLD;
    
    if (file.size < EXTENSION_SIZE_THRESHOLD) {
      console.log('[ExtensionBridge] File below extension threshold, delegating to OPFS transfer');
      throw new Error(`File size ${file.size} bytes below extension threshold ${EXTENSION_SIZE_THRESHOLD} bytes - use OPFS browser transfer`);
    }
    
    if (file.size > 32 * 1024 * 1024) {
      console.warn('[ExtensionBridge] DEPRECATED: processFile() cannot handle files >32MB due to chrome.runtime limits');
      throw new Error(`File size ${file.size} bytes exceeds processFile() 32MB limit - use initializeTransfer + routeChunkToExtension pattern`);
    }
    
    // Verify extension is available
    if (!this.isExtensionAvailable) {
      console.log('[ExtensionBridge] Extension not available, cannot process file');
      throw new Error('Extension not available for file processing - fallback to browser transfer');
    }
    
    console.log('[ExtensionBridge] Extension processing legacy single-operation file');

    // Use streaming for large files to avoid memory bloat
    let fileBuffer: ArrayBuffer;
    if (file.size > 100 * 1024 * 1024) { // > 100MB
      const stream = file.stream();
      const reader = stream.getReader();
      const chunks: Uint8Array[] = [];
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
        }
      } finally {
        reader.releaseLock();
      }
      
      // Combine chunks into ArrayBuffer
      const totalSize = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
      const combined = new Uint8Array(totalSize);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.byteLength;
      }
      fileBuffer = combined.buffer;
    } else {
      fileBuffer = await file.arrayBuffer();
    }
    
    return await this.invokeExtensionWorker('PROCESS_FILE', {
      fileData: fileBuffer,
      fileName: file.name,
      fileSize: file.size,
      options
    });
  }

  /**
   * Resume transfer from checkpoint
   */
  async resumeFromCheckpoint(
    checkpointPath: string,
    options: {
      peerId: string;
      priorityLevel?: 'high' | 'normal' | 'low';
    }
  ): Promise<{
    fileHash: string;
    totalSize: number;
    completedChunks: number[];
    lastProgressUpdate: number;
  }> {
    
    console.log('[ExtensionBridge] Resuming transfer from checkpoint', {
      checkpointPath,
      peerId: options.peerId.slice(0, 8) + '...'
    });

    return await this.invokeExtensionWorker('RESUME_TRANSFER', {
      checkpointPath,
      options
    });
  }

  /**
   * Reassemble file from chunks
   */
  async reassembleFile(
    fileHash: string,
    totalSize: number,
    outputPath: string,
    checkpointPath?: string
  ): Promise<boolean> {
    
    console.log('[ExtensionBridge] Starting file reassembly', {
      fileHash: fileHash.slice(0, 16) + '...',
      totalSize,
      outputPath,
      checkpointPath
    });

    return await this.invokeExtensionWorker('REASSEMBLE_FILE', {
      fileHash,
      totalSize,
      outputPath,
      checkpointPath
    });
  }

  /**
   * Read a chunk from native file through extension
   * This bypasses OPFS entirely for massive file support
   */
  async readNativeFileChunk(
    filePath: string,
    chunkIndex: number,
    chunkSize: number
  ): Promise<Uint8Array | null> {
    
    console.log('[ExtensionBridge] Reading native file chunk', {
      filePath,
      chunkIndex,
      chunkSize
    });

    try {
      const response = await this.invokeExtensionWorker('READ_NATIVE_CHUNK', {
        filePath,
        chunkIndex,
        chunkSize
      });

      if (response.success && response.chunk) {
        // Convert base64 to Uint8Array if needed
        if (typeof response.chunk === 'string') {
          const binaryString = atob(response.chunk);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return bytes;
        }
        return new Uint8Array(response.chunk);
      }
      return null;
    } catch (error) {
      console.error('[ExtensionBridge] Failed to read native chunk:', error);
      return null;
    }
  }

  /**
   * Get transfer progress
   */
  async getTransferProgress(transferId: string): Promise<ProgressUpdate | null> {
    try {
      const progress = await this.invokeExtensionWorker('GET_MASSIVE_FILE_PROGRESS', { transferId });
      return this.mapMassiveProgressToUpdate(progress);
    } catch (error) {
      console.warn('[ExtensionBridge] Failed to get transfer progress:', error);
      return null;
    }
  }

  /**
   * Cleanup extension resources
   */
  async cleanup(transferId?: string): Promise<void> {
    console.log('[ExtensionBridge] Cleaning up extension resources', { transferId });
    
    try {
      if (transferId) {
        try {
          await this.invokeExtensionWorker('CLEANUP_ASSEMBLY', { transferId }, 10000);
        } catch (e) {
          console.warn('[ExtensionBridge] Assembly cleanup warning:', e);
        }
        try {
          await this.invokeExtensionWorker('CLEANUP_PROGRESS_TRACKING', { transferId }, 10000);
        } catch (e) {
          console.warn('[ExtensionBridge] Progress tracking cleanup warning:', e);
        }
        try {
          await this.invokeExtensionWorker('CLEAR_VALIDATION_CACHE', { transferId }, 5000);
        } catch (e) {
          // non-critical
        }
      }
    } catch (error) {
      console.warn('[ExtensionBridge] Cleanup warning (non-critical):', error);
    }

    // Stop keepalive when cleaning up a transfer session
    await this.stopKeepAlive();
  }

  /**
   * Send message to extension
   */
  private sendToExtension(message: ExtensionOperation): void {
    // Use the injected page-bridge API exclusively from page context
    if ('mobyNetExtension' in window && (window as any).mobyNetExtension?.postMessage) {
      try {
        (window as any).mobyNetExtension.postMessage(message)
          .then((res: any) => {
            const response: ExtensionResponse = {
              success: !!res?.success,
              data: res?.data,
              error: res?.error,
              transferId: message.transferId
            };
            this.handleOperationResponse(response);
          })
          .catch((error: any) => {
            this.handleOperationResponse({
              success: false,
              error: error?.message || 'Extension request failed',
              transferId: message.transferId
            });
          });
        return;
      } catch (error) {
        console.warn('[ExtensionBridge] mobyNetExtension.postMessage failed:', error);
      }
    }

    // If we cannot reach the extension via the page bridge, reject the pending operation
    const operation = this.activeOperations.get(message.transferId);
    if (operation) {
      clearTimeout(operation.timeout);
      this.activeOperations.delete(message.transferId);
      operation.reject(new Error('Extension communication unavailable: mobyNetExtension API not found'));
    } else {
      console.error('[ExtensionBridge] No active operation found for transferId when communication failed:', message.transferId);
    }
  }

  /**
   * Register progress update callback
   */
  onProgressUpdate(callback: ProgressCallback): () => void {
    this.progressCallbacks.add(callback);
    return () => this.progressCallbacks.delete(callback);
  }

  /**
   * Register checkpoint update callback
   */
  onCheckpointCreated(callback: CheckpointCallback): () => void {
    this.checkpointCallbacks.add(callback);
    return () => this.checkpointCallbacks.delete(callback);
  }

  /**
   * Register assembly progress callback
   */
  onAssemblyProgress(callback: AssemblyCallback): () => void {
    this.assemblyCallbacks.add(callback);
    return () => this.assemblyCallbacks.delete(callback);
  }

  /**
   * Get extension statistics
   */
  async getExtensionStats(): Promise<{
    activeTransfers: number;
    totalMemoryUsage: number;
    chunksProcessed: number;
    checkpointsCreated: number;
  } | null> {
    try {
      return await this.invokeExtensionWorker('GET_STATS', {}, 5000);
    } catch (error) {
      console.warn('[ExtensionBridge] Failed to get extension stats:', error);
      return null;
    }
  }

  /**
   * Test extension connection
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.pingExtension();
      return response.success === true;
    } catch (error) {
      console.warn('[ExtensionBridge] Extension connection test failed:', error);
      return false;
    }
  }

  /**
   * Format file size for display
   */
  private formatSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }

    return `${value.toFixed(1)} ${units[unitIndex]}`;
  }

  /**
   * Format transfer speed for display
   */
  private formatSpeed(bytesPerSecond: number): string {
    const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    let value = bytesPerSecond;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }

    return `${value.toFixed(1)} ${units[unitIndex]}`;
  }

  /**
   * Initialize sender NFS streaming (for extension transfers)
   * Store file reference for on-demand chunk reading
   */
  async initializeSenderStreaming(params: {
    transferId: string;
    fileName: string;
    fileSize: number;
    file: File;
    chunkSize?: number;  // Accept chunk size from caller
  }): Promise<{ success: boolean; path?: string; error?: string }> {
    try {
      console.log('[ExtensionBridge] Initializing sender NFS streaming:', {
        transferId: params.transferId,
        fileName: params.fileName,
        fileSize: params.fileSize
      });

      // Store the File reference for on-demand chunk reading
      this.senderFileReferences.set(params.transferId, params.file);
      console.log('[ExtensionBridge] Stored file reference for on-demand streaming');

      // Initialize the transfer metadata in extension
      const response = await this.invokeExtensionWorker(
        'INITIALIZE_TRANSFER_METADATA',
        {
          transferId: params.transferId,
          fileName: params.fileName,
          fileSize: params.fileSize,
          direction: 'sender'
          // File will be streamed on-demand, not transferred upfront
        },
        60000 // 60 second timeout for file operations
      );

      // Check if offscreen needs file data for sender streaming
      if (response && response.needsFileData) {
        // Store file metadata for on-demand streaming (not all chunks at once)
        await this.sendFileMetadataForStreaming(params.transferId, params.file, params.chunkSize);
      }

      if (response) {
        console.log('[ExtensionBridge] Sender streaming initialized for on-demand chunk reading');
        return {
          success: true,
          path: response.path || `nfs://${params.transferId}` // Use path from response if available
        };
      }

      return {
        success: false,
        error: 'Failed to initialize sender streaming'
      };
    } catch (error) {
      console.error('[ExtensionBridge] Error initializing sender streaming:', error);
      this.senderFileReferences.delete(params.transferId);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Store file reference and metadata for on-demand streaming
   * No chunking upfront - chunks read on-demand via readSenderChunk
   */
  private async sendFileMetadataForStreaming(transferId: string, file: File, providedChunkSize?: number): Promise<void> {
    try {
      console.log('[ExtensionBridge] Storing file metadata for NFS streaming:', {
        transferId,
        fileName: file.name,
        fileSize: file.size
      });

      // Store file reference locally for on-demand chunk reading
      this.senderFileReferences.set(transferId, file);
      
      // Use provided chunk size or calculate optimal chunk size for this file
      let chunkSize: number;
      if (providedChunkSize && providedChunkSize > 0) {
        chunkSize = providedChunkSize;
        console.log('[ExtensionBridge] Using provided chunk size:', (chunkSize / 1024 / 1024).toFixed(2) + 'MB');
      } else {
        // Fallback: Calculate optimal chunk size for extension transfers
        // Align with provider.tsx chunk size logic for large files
        chunkSize = 4 * 1024 * 1024; // Default 4MB for extension transfers
        if (file.size < 100 * 1024 * 1024) {
          chunkSize = 1024 * 1024; // 1MB for files < 100MB
        }
        if (file.size < 10 * 1024 * 1024) {
          chunkSize = 256 * 1024; // 256KB for files < 10MB
        }
        console.log('[ExtensionBridge] Calculated fallback chunk size:', (chunkSize / 1024 / 1024).toFixed(2) + 'MB');
      }
      
      const totalChunks = Math.ceil(file.size / chunkSize);
      
      // Send only metadata to offscreen, not the file data
      // File data will be read on-demand via READ_SENDER_CHUNK
      await this.invokeExtensionWorker('STORE_SENDER_METADATA', {
        transferId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        chunkSize,
        totalChunks,
        merkleTreeInfo: {
          // Merkle tree will be computed chunk-by-chunk on-demand
          chunkSize,
          totalChunks
        }
      });
      
      console.log('[ExtensionBridge] File metadata stored for on-demand NFS streaming:', {
        transferId,
        chunkSize: (chunkSize / 1024 / 1024).toFixed(2) + 'MB',
        totalChunks
      });
    } catch (error) {
      console.error('[ExtensionBridge] Error storing file metadata:', error);
      throw error;
    }
  }

  /**
   * Read a chunk from sender's file on-demand (true streaming)
   */
  async readSenderChunk(params: {
    transferId: string;
    chunkIndex: number;
    chunkSize: number;
    nfsPath: string;
  }): Promise<Uint8Array | null> {
    try {
      console.log('[ExtensionBridge] Reading sender chunk on-demand:', {
        transferId: params.transferId,
        chunkIndex: params.chunkIndex,
        chunkSize: params.chunkSize
      });

      // Get the file reference
      const file = this.senderFileReferences.get(params.transferId);
      if (!file) {
        console.error('[ExtensionBridge] No file reference found for transfer:', params.transferId);
        return null;
      }

      // Use larger chunk sizes for NFS streaming
      // Override small chunk sizes for better performance
      let actualChunkSize = params.chunkSize;
      if (file.size > 100 * 1024 * 1024 && actualChunkSize < 1024 * 1024) {
        actualChunkSize = 1024 * 1024; // Force 1MB minimum for large files
      }
      if (file.size > 1024 * 1024 * 1024 && actualChunkSize < 4 * 1024 * 1024) {
        actualChunkSize = 4 * 1024 * 1024; // Force 4MB minimum for very large files
      }

      // Calculate chunk boundaries using the actual chunk size
      const start = params.chunkIndex * actualChunkSize;
      const end = Math.min(start + actualChunkSize, file.size);
      
      // Slice only the requested chunk (no memory overhead)
      const chunk = file.slice(start, end);
      const arrayBuffer = await chunk.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Pad the last chunk if needed (for RaptorQ alignment)
      if (uint8Array.length < actualChunkSize && params.chunkIndex < Math.ceil(file.size / actualChunkSize) - 1) {
        const paddedChunk = new Uint8Array(actualChunkSize);
        paddedChunk.set(uint8Array);
        console.log(`[ExtensionBridge] Padded chunk from ${uint8Array.length} to ${actualChunkSize} bytes`);
        return paddedChunk;
      }
      
      console.log(`[ExtensionBridge] Read chunk ${params.chunkIndex} (${uint8Array.length} bytes) with chunk size ${(actualChunkSize/1024/1024).toFixed(2)}MB`);
      return uint8Array;
    } catch (error) {
      console.error('[ExtensionBridge] Error reading sender chunk:', error);
      return null;
    }
  }

  /**
   * Initialize receiver streaming for a file transfer
   */
  async initializeReceiverStreaming(params: {
    transferId: string;
    fileName: string;
    fileSize: number;
    totalChunks: number;
    chunkSize?: number;
  }): Promise<{ success: boolean; error?: string }> {
    console.log('[EXTENSION] Initializing receiver streaming:', params);
    
    try {
      const response = await this.invokeExtensionWorker(
        'INIT_RECEIVER_STREAMING',
        {
          transferId: params.transferId,
          fileName: params.fileName,
          fileSize: params.fileSize,
          totalChunks: params.totalChunks,
          chunkSize: params.chunkSize,
          direction: 'receiver'
        },
        60000 // 60 second timeout for file operations
      );
      
      if (!response || response.error) {
        console.error('[EXTENSION] Receiver streaming initialization failed:', response?.error);
        return { success: false, error: response?.error || 'Failed to initialize receiver streaming' };
      }
      
      console.log('[EXTENSION] Receiver streaming initialized successfully');
      return { success: true };
      
    } catch (error) {
      console.error('[EXTENSION] Error initializing receiver streaming:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Send file data to offscreen document via chunking
   */
  private async sendFileToOffscreen(transferId: string, file: File): Promise<void> {
    const CHUNK_SIZE = 16 * 1024 * 1024; // 16MB chunks (well under 32MB limit)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    console.log(`[ExtensionBridge] Sending file to offscreen in ${totalChunks} chunks`);
    
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      // Convert chunk to ArrayBuffer for transfer
      const arrayBuffer = await chunk.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Send chunk to extension
      await this.invokeExtensionWorker(
        'TRANSFER_FILE_DATA',
        {
          transferId,
          chunkIndex,
          chunkData: Array.from(uint8Array), // Convert to plain array for JSON serialization
          totalChunks,
          isLastChunk: chunkIndex === totalChunks - 1,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size
        },
        30000
      );
      
      console.log(`[ExtensionBridge] Sent chunk ${chunkIndex + 1}/${totalChunks} for transfer ${transferId}`);
    }
    
    console.log(`[ExtensionBridge] All chunks sent for transfer ${transferId}`);
  }

  /**
   * Write a chunk to receiver via offscreen document
   */
  async writeReceiverChunk(params: {
    transferId: string;
    chunkIndex: number;
    chunkData: Uint8Array;
    merkleProof?: any;
    merkleRoot?: string;
    chunkSize?: number;
  }): Promise<{ success: boolean; error?: string }> {
    console.log('[EXTENSION] Writing receiver chunk:', {
      transferId: params.transferId,
      chunkIndex: params.chunkIndex,
      chunkSize: params.chunkData.length
    });
    
    try {
      // Convert Uint8Array to array for message passing
      const chunkArray = Array.from(params.chunkData);
      
      const response = await this.invokeExtensionWorker(
        'WRITE_RECEIVER_CHUNK',
        {
          transferId: params.transferId,
          chunkIndex: params.chunkIndex,
          chunkData: chunkArray,
          merkleProof: params.merkleProof || null,
          merkleRoot: params.merkleRoot || null,
          chunkSize: params.chunkSize
        },
        30000 // 30 second timeout for chunk write
      );
      
      if (!response || response.error) {
        console.error('[EXTENSION] Chunk write failed:', response?.error);
        return { success: false, error: response?.error || 'Failed to write chunk' };
      }
      
      console.log('[EXTENSION] Chunk written successfully');
      return { success: true };
      
    } catch (error) {
      console.error('[EXTENSION] Error writing chunk:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    // Mark disposed to ignore any late async responses/events
    this.disposed = true;
    // Clear all pending operations
    this.activeOperations.forEach(operation => {
      clearTimeout(operation.timeout);
      operation.reject(new Error('ExtensionBridge disposed'));
    });
    this.activeOperations.clear();

    // Clear all callbacks
    this.progressCallbacks.clear();
    this.checkpointCallbacks.clear();
    this.assemblyCallbacks.clear();

    // Remove event listeners
    if (typeof window !== 'undefined') {
      try {
        if (this.boundHandleExtensionMessage) {
          window.removeEventListener('message', this.boundHandleExtensionMessage);
        }
        if (this.extensionReadyListener) {
          window.removeEventListener('mobynet-extension-ready', this.extensionReadyListener);
        }
      } catch (_) {}
      // Remove chrome runtime listener if available
      try {
        if ('chrome' in window && (window as any).chrome?.runtime && this.boundHandleChromeExtensionMessage) {
          (window as any).chrome.runtime.onMessage.removeListener(this.boundHandleChromeExtensionMessage);
        }
      } catch (_) {}
    }

    // Stop keepalive
    // Intentionally fire-and-forget
    this.stopKeepAlive();
  }
}

// Singleton instance
export const extensionBridge = new ExtensionBridge();
